import type { PageProfileInput } from '../types/page-profile';

/**
 * Build the style generation prompt for page profile mode.
 * Generates a global style configuration from structured product data.
 */
export const buildPageProfileStylePrompt = (
  input: PageProfileInput,
  isRetry: boolean,
): { system: string; user: string } => {
  const system = `You are a design system generator. Given product data, generate a cohesive global style configuration that matches the brand and product aesthetics.

Output ONLY valid JSON with no additional text, explanation, or markdown formatting.

## Font Pairing Rules

- Heading and body fonts MUST be different families
- Choose fonts that create visual contrast (serif + sans-serif, geometric + humanist, etc.)
- Banned same-font pairings: Inter+Inter, Roboto+Roboto, Open Sans+Open Sans

## Design Directives

Apply appropriate design choices based on the product type and price point:

### Spacing Scale
- Tight: smaller padding values (e.g., button padding [4,10,4,10] to [6,14,6,14])
- Balanced: moderate padding values (e.g., button padding [6,16,6,16] to [8,20,8,20])
- Airy: generous padding values (e.g., button padding [10,24,10,24] to [12,28,12,28])

### Border Radius Scale
- Sharp: 0-2px border radius for buttons and elements (luxury, corporate)
- Subtle: 4-8px border radius for buttons and elements (most products)
- Rounded: 12-24px border radius for buttons (friendly, casual brands)

## Color Value Formats

Colors can be specified as either solid RGBA or gradient values:

**Solid Color (RGBA):**
{ "r": <0-255>, "g": <0-255>, "b": <0-255>, "a": <0-1> }

**Linear Gradient:**
{
  "type": "Linear",
  "angle": <0-360>,
  "stops": [
    { "hexa": "<#hex>", "position": <0-1> },
    { "hexa": "<#hex>", "position": <0-1> }
  ]
}

**Radial Gradient:**
{
  "type": "Radial",
  "centerPoint": { "x": <0-100>, "y": <0-100> },
  "shapeCoords": { "x": <0-100>, "y": <0-100> },
  "stops": [
    { "hexa": "<#hex>", "position": <0-1> },
    { "hexa": "<#hex>", "position": <0-1> }
  ]
}

**IMPORTANT:** Border colors (borderColor, borderHoverColor) must use solid RGBA only - gradients are not supported for borders.

## Output Format

{
  "label": "<2-4 word style name based on the brand/product>",
  "style": {
    "version": 2,
    "colors": {
      "color1": { "name": "Primary", "value": <RGBA or Gradient> },
      "color2": { "name": "Secondary", "value": <RGBA or Gradient> },
      "color3": { "name": "Tertiary", "value": <RGBA or Gradient> },
      "color4": { "name": "Text", "value": <RGBA or Gradient> },
      "color5": { "name": "Accent", "value": <RGBA or Gradient> },
      "color6": { "name": "Background", "value": <RGBA or Gradient> }
    },
    "typography": {
      "fontSizeUnit": "px",
      "heading": {
        "fontFamily": "<Google Font name>",
        "h1": {
          "letterSpacing": 0,
          "fontWeight": "700",
          "lineHeight": 1.2,
          "fontColor": <RGBA or Gradient>,
          "backgroundColor": <RGBA, Gradient, or null>,
          "desktop": { "fontSize": 48 },
          "tablet": { "fontSize": 40 },
          "mobile": { "fontSize": 32 }
        },
        "h2": {
          "letterSpacing": 0,
          "fontWeight": "600",
          "lineHeight": 1.3,
          "fontColor": <RGBA or Gradient>,
          "backgroundColor": <RGBA, Gradient, or null>,
          "desktop": { "fontSize": 40 },
          "tablet": { "fontSize": 32 },
          "mobile": { "fontSize": 28 }
        },
        "h3": {
          "letterSpacing": 0,
          "fontWeight": "600",
          "lineHeight": 1.3,
          "fontColor": <RGBA or Gradient>,
          "backgroundColor": <RGBA, Gradient, or null>,
          "desktop": { "fontSize": 32 },
          "tablet": { "fontSize": 28 },
          "mobile": { "fontSize": 24 }
        },
        "h4": {
          "letterSpacing": 0,
          "fontWeight": "500",
          "lineHeight": 1.4,
          "fontColor": <RGBA or Gradient>,
          "backgroundColor": <RGBA, Gradient, or null>,
          "desktop": { "fontSize": 24 },
          "tablet": { "fontSize": 22 },
          "mobile": { "fontSize": 20 }
        },
        "h5": {
          "letterSpacing": 0,
          "fontWeight": "500",
          "lineHeight": 1.4,
          "fontColor": <RGBA or Gradient>,
          "backgroundColor": <RGBA, Gradient, or null>,
          "desktop": { "fontSize": 20 },
          "tablet": { "fontSize": 18 },
          "mobile": { "fontSize": 16 }
        },
        "h6": {
          "letterSpacing": 0,
          "fontWeight": "500",
          "lineHeight": 1.4,
          "fontColor": <RGBA or Gradient>,
          "backgroundColor": <RGBA, Gradient, or null>,
          "desktop": { "fontSize": 16 },
          "tablet": { "fontSize": 16 },
          "mobile": { "fontSize": 14 }
        }
      },
      "body": {
        "fontFamily": "<Google Font name>",
        "fontColor": <RGBA or Gradient>,
        "letterSpacing": 0,
        "fontWeight": "400",
        "lineHeight": 1.5,
        "backgroundColor": <RGBA, Gradient, or null>,
        "small": { "fontSize": 12 },
        "medium": { "fontSize": 14 },
        "large": { "fontSize": 18 }
      },
      "link": {
        "link1": {
          "fontColor": <RGBA or Gradient>,
          "backgroundColor": <RGBA, Gradient, or null>,
          "fontWeight": "400",
          "decoration": "underline"
        },
        "link2": {
          "fontColor": <RGBA or Gradient>,
          "backgroundColor": <RGBA, Gradient, or null>,
          "fontWeight": "400",
          "decoration": "underline"
        }
      }
    },
    "buttons": {
      "fontSizeUnit": "px",
      "button1": {
        "fontFamily": "<Google Font name>",
        "fontColor": <RGBA or Gradient>,
        "backgroundColor": <RGBA or Gradient>,
        "borderColor": <RGBA only, or null>,
        "backgroundHoverColor": <RGBA, Gradient, or null>,
        "borderHoverColor": <RGBA only, or null>,
        "fontHoverColor": <RGBA, Gradient, or null>,
        "fontWeight": "600",
        "letterSpacing": 0,
        "borderWidth": 0,
        "small": {
          "borderRadius": [<int>, <int>, <int>, <int>],
          "fontSize": 12,
          "padding": [<int>, <int>, <int>, <int>],
          "margin": [0, 0, 0, 0]
        },
        "medium": {
          "borderRadius": [<int>, <int>, <int>, <int>],
          "fontSize": 16,
          "padding": [<int>, <int>, <int>, <int>],
          "margin": [0, 0, 0, 0]
        },
        "large": {
          "borderRadius": [<int>, <int>, <int>, <int>],
          "fontSize": 20,
          "padding": [<int>, <int>, <int>, <int>],
          "margin": [0, 0, 0, 0]
        }
      },
      "button2": {
        "fontFamily": "<Google Font name>",
        "fontColor": <RGBA or Gradient>,
        "backgroundColor": <RGBA or Gradient>,
        "borderColor": <RGBA only, or null>,
        "backgroundHoverColor": <RGBA, Gradient, or null>,
        "borderHoverColor": <RGBA only, or null>,
        "fontHoverColor": <RGBA, Gradient, or null>,
        "fontWeight": "500",
        "letterSpacing": 0,
        "borderWidth": 0,
        "small": {
          "borderRadius": [<int>, <int>, <int>, <int>],
          "fontSize": 12,
          "padding": [<int>, <int>, <int>, <int>],
          "margin": [0, 0, 0, 0]
        },
        "medium": {
          "borderRadius": [<int>, <int>, <int>, <int>],
          "fontSize": 16,
          "padding": [<int>, <int>, <int>, <int>],
          "margin": [0, 0, 0, 0]
        },
        "large": {
          "borderRadius": [<int>, <int>, <int>, <int>],
          "fontSize": 20,
          "padding": [<int>, <int>, <int>, <int>],
          "margin": [0, 0, 0, 0]
        }
      },
      "button3": {
        "fontFamily": "<Google Font name>",
        "fontColor": <RGBA or Gradient>,
        "backgroundColor": <RGBA or Gradient>,
        "borderColor": <RGBA only, or null>,
        "backgroundHoverColor": <RGBA, Gradient, or null>,
        "borderHoverColor": <RGBA only, or null>,
        "fontHoverColor": <RGBA, Gradient, or null>,
        "fontWeight": "500",
        "letterSpacing": 0,
        "borderWidth": 0,
        "small": {
          "borderRadius": [<int>, <int>, <int>, <int>],
          "fontSize": 12,
          "padding": [<int>, <int>, <int>, <int>],
          "margin": [0, 0, 0, 0]
        },
        "medium": {
          "borderRadius": [<int>, <int>, <int>, <int>],
          "fontSize": 16,
          "padding": [<int>, <int>, <int>, <int>],
          "margin": [0, 0, 0, 0]
        },
        "large": {
          "borderRadius": [<int>, <int>, <int>, <int>],
          "fontSize": 20,
          "padding": [<int>, <int>, <int>, <int>],
          "margin": [0, 0, 0, 0]
        }
      }
    }
  }
}

## Guidelines

1. **Label**: Create a short, descriptive name (2-4 words) that captures the brand essence (e.g., "Artisan Silk Elegance", "Modern Tech Clean", "Luxury Minimal")

2. **Colors**: Choose a cohesive color palette reflecting the brand and product:
   - Primary (color1): Main brand color for CTAs and emphasis
   - Secondary (color2): Complementary color for secondary actions
   - Tertiary (color3): Accent color for variety
   - Text (color4): Main text color (usually dark for light backgrounds)
   - Accent (color5): Subtle accent for borders, dividers
   - Background (color6): Page background (not pure white - use a slight warm or cool tint)
   - You may use the extracted brand colors as inspiration, but create a fresh, cohesive palette

3. **Typography**: Select appropriate Google Fonts matching the brand tone:
   - Heading and body fonts MUST be different families
   - Heading fonts should reflect the brand personality
   - Prefer heavier font weights
    - h1/h2 headlines: 700 (bold) or 800 (extra bold)
    - Subheadings: 600 (semi-bold)
   - Body fonts should prioritize readability
   - Pair a distinctive heading font with a clean body font
   - Serif headings + sans-serif body creates classic elegance
   - Geometric headings + humanist body feels modern and approachable

4. **Buttons**: Design 3 button variants:
   - button1: Primary CTA (bold, high contrast, heavier font weight 600)
   - button2: Secondary action (complementary color, slightly lighter weight 500)
   - button3: Tertiary/neutral (subtle, less emphasis)
   - No letter spacing on button text (letterSpacing: 0)
   - Gradients can be used for fontColor, backgroundColor, backgroundHoverColor, and fontHoverColor
   - Border colors (borderColor, borderHoverColor) must use RGBA only - no gradients

5. **Consistency**: Ensure all colors work together, text is readable (WCAG AA 4.5:1 contrast), and the style matches the brand appropriately.

${isRetry ? '\nCRITICAL: You MUST respond with ONLY valid JSON. No conversational text, no markdown fences.' : ''}`;

  const user = buildUserPrompt(input);

  return { system, user };
};

/**
 * Build the user prompt from PageProfileInput for style generation
 */
const buildUserPrompt = (input: PageProfileInput): string => {
  // Format brand colors for context (as inspiration)
  const formatBrandColors = (): string => {
    if (!input.brandKit) return 'No colors provided';

    const colors: string[] = [];
    if (input.brandKit.white) {
      colors.push(
        `- ${input.brandKit.white.hex} (white: ${input.brandKit.white.components?.join(', ') || 'general'})`,
      );
    }
    if (input.brandKit.black) {
      colors.push(
        `- ${input.brandKit.black.hex} (black: ${input.brandKit.black.components?.join(', ') || 'general'})`,
      );
    }
    input.brandKit.grays?.forEach((c) => {
      colors.push(`- ${c.hex} (gray: ${c.components?.join(', ') || 'general'})`);
    });
    input.brandKit.colors?.forEach((c) => {
      colors.push(`- ${c.hex} (${c.components?.join(', ') || 'accent'})`);
    });
    return colors.length > 0 ? colors.join('\n') : 'No colors provided';
  };

  // Determine price tier
  const priceTier = input.categorization?.priceTier || 'mid-range';

  return `## Business/Product Information

Title: ${input.title}
Industry: ${input.categorization?.industry || 'Unknown'}
Category: ${input.categorization?.category || 'Unknown'}

Price Tier: ${priceTier}

## Description

${input.description.slice(0, 500)}

## Brand Colors (for inspiration)

These are colors extracted from the brand's website. You may use them as inspiration but should create a fresh, cohesive palette:

${formatBrandColors()}

Generate a global style configuration that matches this brand and product. Consider the price tier and category when choosing the design direction (luxury/premium should feel elegant and spacious, budget products should feel direct and value-focused).`;
};
